<template>
  <div class="xs12 py-3 flex align-center entry">
    <div style="flex:4;min-width:0;">
      <DateTimePicker
        :range="true"
        :value="[value.start, value.end]"
        class="mr-2"
        :viewTime="true"
        :allow-no-end="true"
        @input="updateEntryStartEnd($event)"
      ></DateTimePicker>
    </div>
    <div style="flex:1;min-width:0;">
      <div class="ml-2 button-i disabled text-ellipsis">
        {{ countHours(value.start, value.end) }}
      </div>
    </div>
    <div style="flex:2;min-width:0;">
      <div
        v-tooltip="'Edit entry description'"
        class="ml-2 mt-1 button-i text-ellipsis"
        :style="!value.description ? 'font-style: italic' : ''"
        @click="startEditTimeEntryDescription(value)"
        v-html="
          getFirstLineFromHtml(value.description)
            ? getFirstLineFromHtml(value.description)
            : `<span style='font-style:italic'>No description</span>`
        "
      ></div>
    </div>
    <div style="flex:2;min-width:0;">
      <Select
        class="mx-2"
        :value="value.projectId"
        :items="projectItems"
        @input="onProjectChange($event)"
      ></Select>
    </div>
    <div style="flex:1;min-width:0;">
      <input
        class="earning-input mx-2"
        type="number"
        min="0"
        step="0.01"
        :value="value.earningPerHour"
        placeholder="0.00"
        v-tooltip="'Earning per hour'"
        @change="updateEntry('earningPerHour', $event.target.value ? parseFloat($event.target.value) : null)"
      />
    </div>
    <div style="flex: 0 0 88px;" class="flex">
      <i
        v-tooltip="'Duplicate entry'"
        class="icofont-ui-copy button-i ml-3"
        @click="duplicateEntry(value)"
        v-if="!loadingDuplicate"
      ></i>
      <Loading v-else class="ml-3" size="16"></Loading>
      <i
        v-tooltip="'Delete entry'"
        class="icofont-ui-delete button-i ml-3"
        @click="dialogs.confirmDeleteTimeEntry.dialog = true"
      ></i>
      <i
        v-tooltip="'More settings for entry'"
        class="icofont-options button-i ml-3"
      ></i>
    </div>
    <Dialog
      class="black-text"
      v-for="(dialog, key) of dialogs"
      :key="key"
      :value="dialog.dialog"
      @close="
        () => {
          dialog.dialog = false
          dialog.hasChanges = false
        }
      "
      :persistent="dialog.loading || (dialog.changeGuard && dialog.hasChanges)"
    >
      <DataForm
        :type="dialog.type"
        :value="dialog.form"
        :loading="dialog.loading"
        :disabled="dialog.loading"
        :allowCancel="true"
        @cancel="dialog.dialog = false"
        @update="dialogOk(key, $event)"
        :title="dialog.title"
        @updateHasChanges="dialog.hasChanges = $event"
      ></DataForm>
    </Dialog>
  </div>
</template>

<script>
  import moment from 'moment'
  import Select from '../common/Select.vue'
  import DateTimePicker from '../common/DateTimePicker.vue'
  import Dialog from '../common/Dialog.vue'
  import DataForm from '../common/DataForm.vue'
  import Loading from '../common/Loading.vue'
  import { getFirstLineFromHtml, durationFormat } from '@/utils'

  export default {
    name: 'ReportEntry',
    components: {
      DateTimePicker,
      Select,
      Dialog,
      DataForm,
      Loading
    },
    props: ['value'],
    data: () => ({
      loadingDuplicate: false,
      dialogs: {
        confirmDeleteTimeEntry: {
          dialog: false,
          loading: false,
          title: 'Are you sure you want to delete the time entry?',
          type: 'yesno',
          form: {}
        },
        timeEntryDescriptionEditor: {
          dialog: false,
          loading: false,
          title: 'Edit entry description',
          type: 'edit',
          selectedEntry: null,
          changeGuard: true,
          hasChanges: false,
          form: {
            description: {
              type: 'textarea',
              label: 'Description',
              default: ''
            }
          }
        }
      }
    }),
    computed: {
      projects() {
        return this.$store.getters.projects
      },
      projectItems() {
        return this.$store.getters.projectItems
      }
    },
    methods: {
      countHours(start, end) {
        const duration = moment.duration(
          moment(end || undefined).diff(moment(start))
        )
        return (
          durationFormat(start, end || undefined) +
          ' (' +
          parseFloat(Math.round(duration.asHours() * 100) / 100) +
          ')'
        )
      },
      getFirstLineFromHtml,
      formatDateTime(datetime) {
        return moment(datetime).format('DD.MM.YYYY HH:mm:ss')
      },
      async onProjectChange(projectId) {
        const payload = { id: this.value.id, projectId }
        // Auto-fill earningPerHour from project default if current value is 0 or null
        if (!this.value.earningPerHour) {
          const project = this.projects.find((p) => p.id === projectId)
          if (project && project.defaultEarningPerHour) {
            payload.earningPerHour = project.defaultEarningPerHour
          }
        }
        await this.$store.dispatch('updateTimeEntry', payload)
      },
      async dialogOk(key, payload) {
        this.dialogs[key].loading = true
        if (key === 'timeEntryDescriptionEditor') {
          const timeEntry =
            this.dialogs.timeEntryDescriptionEditor.selectedEntry
          await this.$store.dispatch('updateTimeEntry', {
            id: timeEntry.id,
            ...payload
          })
        } else if (key === 'confirmDeleteTimeEntry') {
          await this.$store.dispatch('deleteTimeEntry', this.value.id)
        }
        this.dialogs[key].hasChanges = false
        this.dialogs[key].loading = false
        this.dialogs[key].dialog = false
      },
      startEditTimeEntryDescription(timeEntry) {
        const key = 'timeEntryDescriptionEditor'
        this.dialogs[key].selectedEntry = timeEntry
        this.dialogs[key].form.description.default = timeEntry.description
        this.dialogs[key].hasChanges = false
        this.dialogs[key].dialog = true
      },
      async duplicateEntry(entry) {
        this.loadingDuplicate = true
        entry = JSON.parse(JSON.stringify(entry))
        delete entry.id
        await this.$store.dispatch('createTimeEntry', entry)
        this.loadingDuplicate = false
      },
      async updateEntry(prop, value) {
        const payload = { id: this.value.id }
        payload[prop] = value
        await this.$store.dispatch('updateTimeEntry', payload)
      },
      async updateEntryStartEnd(startEnd) {
        const payload = {
          id: this.value.id,
          start: startEnd[0],
          end: startEnd[1]
        }
        await this.$store.dispatch('updateTimeEntry', payload)
      }
    }
  }
</script>

<style scoped>
  .entry:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .earning-input {
    width: 100%;
    border: 1px solid var(--app-border);
    border-radius: var(--app-radius-md);
    background: var(--app-bg-input);
    padding: 6px 8px;
    font-size: 0.875rem;
    color: var(--app-text);
    outline: none;
    transition: border-color var(--app-transition);
  }

  .earning-input:focus {
    border-color: var(--primary);
  }

  .earning-input::placeholder {
    color: var(--app-text-muted);
  }
</style>
